import { useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import TopicCard from "@/components/feature/TopicCard";
import useSeo from "@/hooks/useSeo";
import { categories, topics } from "@/mocks/topics";

export default function Topics() {
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("cat") ?? "all";
  const query = params.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);

  useSeo({
    title: "热点复盘列表 · 按分类与关键词查找事件时间线 | 黑料热点",
    description:
      "浏览黑料热点已完成时间线梳理的事件复盘：支持按品牌风波、活动现场、内容争议、电商消费、版权纠纷、消费质疑分类筛选，也可用关键词快速检索关心的热点脉络。",
    keywords: "热点复盘列表,事件时间线,黑料热点分类,热点事件检索,事件复盘合集",
    canonicalPath: "/topics",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "热点复盘列表",
        description: "按分类与关键词浏览已完成时间线梳理的热点事件复盘。",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "/" },
          { "@type": "ListItem", position: 2, name: "热点复盘", item: "/topics" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: topics.map((topic, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: topic.title,
          url: `/topic/${topic.id}`,
        })),
      },
    ],
  });

  const filtered = useMemo(() => {
    const kw = query.trim().toLowerCase();
    return topics.filter((topic) => {
      const matchCat = activeCat === "all" || topic.categoryId === activeCat;
      const matchKw =
        !kw ||
        topic.title.toLowerCase().includes(kw) ||
        topic.summary.toLowerCase().includes(kw) ||
        topic.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCat && matchKw;
    });
  }, [activeCat, query]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setParams(next, { replace: true });
  };

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updateParam("q", keyword.trim());
  };

  const handleCat = (catId: string) => {
    updateParam("cat", catId === "all" ? "" : catId);
  };

  return (
    <div className="min-h-screen w-full bg-background-50">
      <SiteHeader />
      <main>
        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-8 md:py-12">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "热点复盘" }]} />
            <h1 className="mt-4 font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-4xl">
              热点复盘列表
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
              这里收录了已完成时间线梳理的事件复盘。先按分类缩小范围，或用关键词直接搜索事件、品牌或争议关键词。
            </p>

            <form onSubmit={handleSearch} className="mt-6 flex flex-col gap-3 sm:flex-row">
              <div className="relative flex-1">
                <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lg text-foreground-400"></i>
                <input
                  type="search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索事件名称、品牌或争议关键词"
                  aria-label="搜索热点事件"
                  className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-10 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <button
                type="submit"
                className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索复盘
              </button>
            </form>
          </div>
        </section>

        <section className="w-full">
          <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-8 md:py-14">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => handleCat("all")}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  activeCat === "all"
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                }`}
              >
                全部分类
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => handleCat(cat.id)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    activeCat === cat.id
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                  }`}
                >
                  <i className={cat.icon}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="mt-6 flex items-center justify-between border-b border-background-200 pb-4 text-sm text-foreground-500">
              <span>
                共 <strong className="text-foreground-900">{filtered.length}</strong> 起复盘
                {query && ` · 关键词「${query}」`}
              </span>
              {(activeCat !== "all" || query) && (
                <button
                  type="button"
                  onClick={() => {
                    setKeyword("");
                    setParams(new URLSearchParams(), { replace: true });
                  }}
                  className="flex items-center gap-1 text-primary-600 transition-colors hover:text-primary-700"
                >
                  <i className="ri-close-circle-line"></i>
                  清除筛选
                </button>
              )}
            </div>

            {filtered.length > 0 ? (
              <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map((topic) => (
                  <TopicCard key={topic.id} topic={topic} />
                ))}
              </div>
            ) : (
              <div className="mt-12 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-search-eye-line text-3xl"></i>
                </span>
                <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                  没有找到匹配的复盘
                </h2>
                <p className="mt-2 max-w-md text-sm text-foreground-600">
                  换个关键词，或选择其他分类再试试。我们每周都会补充新的事件时间线。
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setKeyword("");
                    setParams(new URLSearchParams(), { replace: true });
                  }}
                  className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  查看全部复盘
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}